import { useEffect, useRef, type RefObject } from "react";

import { NESTED_INTERACTIVE_SELECTOR } from "../../utils/nestedInteractive";
import { MODAL_EXITING_ATTR, topmostModalEl } from "../Modal/modalStack";

interface PlaybackSpaceOwner {
  root: HTMLElement;
  getScope: () => HTMLElement | null;
  documentFallback: boolean;
  isEnabled: () => boolean;
  onKeyDown: (event: KeyboardEvent) => boolean;
}

interface PlaybackSpaceRegistry {
  eventTarget: EventTarget;
  listener: EventListener;
  owners: Set<PlaybackSpaceOwner>;
}

const registries = new WeakMap<Document, PlaybackSpaceRegistry>();

const LOCAL_TIMELINE_SPACE_TARGET = "[data-media-timeline-input], [data-media-selection-grip]";
const SPACE_OWNING_INTERACTIVE_SELECTOR = [
  NESTED_INTERACTIVE_SELECTOR,
  "area[href]",
  "audio[controls]",
  "video[controls]",
  "summary",
  "embed",
  "object",
  "[contenteditable]:not([contenteditable='false'])",
].join(", ");

function eventElement(ownerDocument: Document, target: EventTarget | null): Element | null {
  const ElementConstructor = ownerDocument.defaultView?.Element;
  return ElementConstructor && target instanceof ElementConstructor ? (target as Element) : null;
}

function resolveShortcutTarget(ownerDocument: Document, event: KeyboardEvent): Element | null {
  const target = eventElement(ownerDocument, event.target);
  if (target && target !== ownerDocument.body && target !== ownerDocument.documentElement) {
    return target;
  }
  return eventElement(ownerDocument, ownerDocument.activeElement) ?? target;
}

function findContainingOwner(
  owners: Set<PlaybackSpaceOwner>,
  target: Element | null,
): PlaybackSpaceOwner | null {
  if (!target) return null;

  let match: PlaybackSpaceOwner | null = null;
  let ambiguous = false;
  for (const owner of owners) {
    const scope = owner.getScope();
    if (!scope?.contains(target)) continue;
    const matchedScope = match?.getScope();
    if (matchedScope === scope) {
      ambiguous = true;
    } else if (!matchedScope || matchedScope.contains(scope)) {
      match = owner;
      ambiguous = false;
    }
  }
  // Equal scopes cannot choose a transport. Keep the scope claimed, but do not execute it.
  return ambiguous && match ? { ...match, isEnabled: () => false } : match;
}

function resolveOwner(
  owners: Set<PlaybackSpaceOwner>,
  target: Element | null,
): PlaybackSpaceOwner | null {
  const containingOwner = findContainingOwner(owners, target);
  if (containingOwner) {
    return containingOwner.isEnabled() && !ownerIsBlocked(containingOwner, target)
      ? containingOwner
      : null;
  }

  const eligibleOwners = Array.from(owners).filter(
    (owner) => owner.documentFallback && owner.isEnabled() && !ownerIsBlocked(owner, target),
  );
  return eligibleOwners.length === 1 ? (eligibleOwners[0] ?? null) : null;
}

function targetKeepsNativeSpace(owner: PlaybackSpaceOwner, target: Element | null): boolean {
  if (!target) return false;
  const interactiveTarget = target.closest(SPACE_OWNING_INTERACTIVE_SELECTOR);
  if (!interactiveTarget) return false;

  // The playhead range and selection handles are timeline navigation surfaces. The range owns
  // Space locally before bubbling; the handle deliberately falls through to this wider scope.
  return !(
    owner.root.contains(interactiveTarget) && interactiveTarget.matches(LOCAL_TIMELINE_SPACE_TARGET)
  );
}

function isLayoutHidden(root: HTMLElement): boolean {
  if (
    !root.isConnected ||
    root.closest(`[inert], [hidden], [aria-hidden='true'], [${MODAL_EXITING_ATTR}]`)
  )
    return true;

  const view = root.ownerDocument.defaultView;
  if (!view) return false;

  for (let element: HTMLElement | null = root; element; element = element.parentElement) {
    const style = view.getComputedStyle(element);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.visibility === "collapse" ||
      style.getPropertyValue("content-visibility") === "hidden"
    ) {
      return true;
    }
  }
  return false;
}

function ownerIsBlocked(owner: PlaybackSpaceOwner, target: Element | null): boolean {
  const scope = owner.getScope();
  if (!scope || !scope.contains(owner.root)) {
    return true;
  }
  return isPlaybackSpaceBlocked(owner.root, target);
}

/** Also used by the local seek handler, which runs before the shared window listener. */
export function isPlaybackSpaceBlocked(root: HTMLElement, target: Element | null): boolean {
  if (isLayoutHidden(root)) return true;
  const topModal = topmostModalEl();
  if (topModal?.ownerDocument === root.ownerDocument && !topModal.contains(root)) {
    return true;
  }

  // An active modal outside this timeline owns the keyboard. A timeline rendered inside that
  // modal remains eligible, including when the modal itself is the current key target.
  const activeModal = target?.closest('[role="dialog"][aria-modal="true"]') ?? null;
  return activeModal !== null && !activeModal.contains(root);
}

function handleGlobalSpace(
  ownerDocument: Document,
  owners: Set<PlaybackSpaceOwner>,
  event: KeyboardEvent,
): void {
  if (
    event.defaultPrevented ||
    event.isComposing ||
    event.keyCode === 229 ||
    (event.code !== "Space" && event.key !== " ")
  ) {
    return;
  }

  const target = resolveShortcutTarget(ownerDocument, event);
  const owner = resolveOwner(owners, target);
  if (!owner || targetKeepsNativeSpace(owner, target)) {
    return;
  }

  owner.onKeyDown(event);
}

function registerPlaybackSpaceOwner(owner: PlaybackSpaceOwner): () => void {
  const ownerDocument = owner.root.ownerDocument;
  let registry = registries.get(ownerDocument);
  if (!registry) {
    const owners = new Set<PlaybackSpaceOwner>();
    const listener: EventListener = (event) =>
      handleGlobalSpace(ownerDocument, owners, event as KeyboardEvent);
    const eventTarget: EventTarget = ownerDocument.defaultView ?? ownerDocument;
    registry = { eventTarget, listener, owners };
    registries.set(ownerDocument, registry);
    eventTarget.addEventListener("keydown", listener);
  }

  registry.owners.add(owner);
  return () => {
    registry?.owners.delete(owner);
    if (registry?.owners.size !== 0) return;
    registry.eventTarget.removeEventListener("keydown", registry.listener);
    registries.delete(ownerDocument);
  };
}

/**
 * Shared Space ownership for explicit basic-player scopes and the rich timeline's document
 * fallback. Resolve the scope ref at event time: portal mount/replacement can change `.current`
 * without rerendering the bar. Local controls get first refusal before this window listener.
 */
export function usePlaybackSpaceShortcut(
  rootRef: RefObject<HTMLElement | null>,
  onKeyDown: (event: KeyboardEvent) => boolean,
  enabled: boolean,
  scopeRef?: RefObject<HTMLElement | null>,
  documentFallback = false,
): void {
  const handlerRef = useRef(onKeyDown);
  const enabledRef = useRef(enabled);
  const scopeSourceRef = useRef(scopeRef);
  handlerRef.current = onKeyDown;
  enabledRef.current = enabled;
  scopeSourceRef.current = scopeRef;

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !scopeRef) return;
    return registerPlaybackSpaceOwner({
      root,
      getScope: () => scopeSourceRef.current?.current ?? null,
      documentFallback,
      isEnabled: () => enabledRef.current,
      onKeyDown: (event) => handlerRef.current(event),
    });
  }, [rootRef, scopeRef, documentFallback]);
}
